import { ESymbolStatus, ESymbolType, ISymbol } from '@webtrader/core/api/market/symbols';
import { ESwapType, ETradeMode, ITradeFillPolicy } from '@webtrader/core/api/market/symbols/detail';
import { EOrderTypeLabel } from '@webtrader/core/constants/order';
import { EMarketFilter, TSymbolFilter } from '@webtrader/core/constants/symbol';
import i18n from '@webtrader/core/i18n';

import { ETradeFillPolicy } from '../api/trade';

export const mapSymbolTypeToText = (type: ESymbolType) => {
  switch (type) {
    case ESymbolType.COMMODITIES:
      return i18n.t('Commodities');
    case ESymbolType.CRYPTO:
      return i18n.t('Crypto');
    case ESymbolType.FOREX:
      return i18n.t('Forex');
    case ESymbolType.INDEX:
      return i18n.t('Index');
    case ESymbolType.SHARES:
      return i18n.t('Shares');
    default:
      return i18n.t('Others');
  }
};

export const mapTradeModeToText = (tradeMode: ETradeMode) => {
  switch (tradeMode) {
    case ETradeMode.TRADE_NO:
      return i18n.t('This symbol is for view only.');
    case ETradeMode.TRADE_CLOSE:
      return i18n.t('Closed Access');
    case ETradeMode.TRADE_FULL:
      return i18n.t('Full Access');
    default:
      return '';
  }
};

export const mapStatusToText = (status: ESymbolStatus) => {
  switch (status) {
    case ESymbolStatus.CLOSED:
      return () => i18n.t('Closed');
    case ESymbolStatus.TRADING:
      return () => i18n.t('Trading');
    case ESymbolStatus.OFFQUOTES:
      return () => i18n.t('Off Quotes');
    default:
      return () => '';
  }
};

export const isAllowedToTrade = (status: ESymbolStatus) => {
  return status === ESymbolStatus.TRADING;
};

export const symbolTypeToMarketFilter = (type: ESymbolType) => {
  switch (type) {
    case ESymbolType.FOREX:
      return EMarketFilter.FOREX;
    case ESymbolType.INDEX:
      return EMarketFilter.INDEX;
    case ESymbolType.CRYPTO:
      return EMarketFilter.CRYPTO;
    case ESymbolType.SHARES:
      return EMarketFilter.SHARES;
    case ESymbolType.COMMODITIES:
      return EMarketFilter.COMMODITIES;
    case ESymbolType.OTHERS:
      return EMarketFilter.OTHERS;
    default:
      return EMarketFilter.FAVOURITES;
  }
};

export const filterSymbols = (symbols: ISymbol[]) => {
  const indexSymbols = symbols.filter((s) => s.type === ESymbolType.INDEX);
  const cryptoSymbols = symbols.filter((s) => s.type === ESymbolType.CRYPTO);
  const forexSymbols = symbols.filter((s) => s.type === ESymbolType.FOREX);
  const sharesSymbols = symbols.filter((s) => s.type === ESymbolType.SHARES);
  const favouriteSymbols = symbols.filter((s) => s.watch);
  const commoditiesSymbols = symbols.filter((s) => s.type === ESymbolType.COMMODITIES);

  return {
    indexSymbols,
    cryptoSymbols,
    forexSymbols,
    sharesSymbols,
    favouriteSymbols,
    commoditiesSymbols
  };
};

export const filterSymbolsByMarketFilter = (filter: TSymbolFilter, symbols: ISymbol[]) => {
  switch (filter) {
    case EMarketFilter.FOREX: {
      return symbols.filter((s) => s.type === ESymbolType.FOREX);
    }
    case EMarketFilter.INDEX: {
      return symbols.filter((s) => s.type === ESymbolType.INDEX);
    }
    case EMarketFilter.CRYPTO: {
      return symbols.filter((s) => s.type === ESymbolType.CRYPTO);
    }
    case EMarketFilter.SHARES: {
      return symbols.filter((s) => s.type === ESymbolType.SHARES);
    }
    case EMarketFilter.COMMODITIES: {
      return symbols.filter((s) => s.type === ESymbolType.COMMODITIES);
    }
    case EMarketFilter.FAVOURITES: {
      return symbols.filter((s) => s.watch);
    }
    case EMarketFilter.ALL: {
      return symbols;
    }
    default:
      return [];
  }
};

export const safetyCheckForNumberValues = (value?: number) => {
  return value === undefined || value === null || Number.isNaN(value) ? 0 : value;
};

export const SWAP_TYPE_TEXT_MAP: Record<ESwapType, () => string> = {
  [ESwapType.SWAP_BY_POINTS]: () => i18n.t('in points'),
  [ESwapType.SWAP_BY_DOLLARS]: () => i18n.t('in money, using base currency'),
  [ESwapType.SWAP_BY_INTEREST]: () => i18n.t('in percentage term'),
  [ESwapType.SWAP_BY_MARGIN_CURRENCY]: () => i18n.t('in money, using margin currency'),
  [ESwapType.SWAP_DISABLED]: () => '-',
  [ESwapType.SWAP_BY_SYMBOL_CURRENCY]: () => i18n.t('in money, using margin currency'),
  [ESwapType.SWAP_BY_GROUP_CURRENCY]: () => i18n.t('in money, using group currency'),
  [ESwapType.SWAP_BY_INTEREST_CURRENT]: () => i18n.t('in percentage terms, using current price'),
  [ESwapType.SWAP_BY_INTEREST_OPEN]: () => i18n.t('in percentage terms, using open price'),
  [ESwapType.SWAP_REOPEN_BY_CLOSE_PRICE]: () => i18n.t('in points, reopen position by close price'),
  [ESwapType.SWAP_REOPEN_BY_BID]: () => i18n.t('in points, reopen position by bid price'),
  [ESwapType.SWAP_BY_PROFIT_CURRENCY]: () => i18n.t('in money, using profit currency')
};

export const tradeButtonStatus = (orderTypeLabel?: EOrderTypeLabel, tradeFillPolicy?: ITradeFillPolicy) => {
  if (!orderTypeLabel || !tradeFillPolicy) {
    return true;
  }

  if (orderTypeLabel === EOrderTypeLabel.LIMIT) {
    return !tradeFillPolicy?.limitFillPolicy || tradeFillPolicy?.limitFillPolicy?.length === 0;
  } else if (orderTypeLabel === EOrderTypeLabel.STOP) {
    return !tradeFillPolicy?.stopFillPolicy || tradeFillPolicy?.stopFillPolicy?.length === 0;
  } else if (orderTypeLabel === EOrderTypeLabel.STOP_LIMIT) {
    return !tradeFillPolicy?.stopLimitFillPolicy || tradeFillPolicy.stopLimitFillPolicy?.length === 0;
  } else {
    return !tradeFillPolicy?.marketFillPolicy || tradeFillPolicy?.marketFillPolicy?.length === 0;
  }
};

export const oneClickAutoSelectFillPolicy = (orderTypeLabel?: EOrderTypeLabel, tradeFillPolicy?: ITradeFillPolicy) => {
  if (!orderTypeLabel || !tradeFillPolicy) {
    return undefined;
  }

  let policies: ETradeFillPolicy[] = [];

  if (orderTypeLabel === EOrderTypeLabel.LIMIT) {
    policies = tradeFillPolicy?.limitFillPolicy ?? [];
  } else if (orderTypeLabel === EOrderTypeLabel.STOP) {
    policies = tradeFillPolicy?.stopFillPolicy ?? [];
  } else if (orderTypeLabel === EOrderTypeLabel.STOP_LIMIT) {
    policies = tradeFillPolicy?.stopLimitFillPolicy ?? [];
  } else {
    policies = tradeFillPolicy?.marketFillPolicy ?? [];
  }

  return policies && policies?.length > 0 ? policies[0] : undefined;
};
